import {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  useMemo,
} from 'react';
import {
  PagedDataGrid,
  FilterType,
  FilterOperator,
  buildMetadataFilterColumns,
  StatusBadge,
  type IPagedDataGridRefType,
  type DataGridColumn,
  type ICommandBarItem,
  type IFilterableColumn,
} from '@moko/ui';
import { createWorkOfferBatch, formatDate } from '@moko/utils';
import { getChartsData } from '../../services/dashboardService';
import { IField, ILookup, getMetadata } from '../../services/metadataService';
import { deleteGroup } from '../../services/groupService';
import {
  IWork,
  IWorkAssignmentRequest,
  IWorkStatus,
  createWorkAssignment,
  deleteWork,
  getWorkAssignment,
  getWorkStatuses,
  getWorks,
  updateWorkAssignments,
} from '../../services/workService';
import { useAtomValue, useSetAtom } from 'jotai';
import { useNavigate } from 'react-router-dom';
import { profileDataAtom } from '../../atoms/authAtoms';
import {
  clearMessageAtom,
  errorMessageAtom,
  isInProgressAtom,
  messageBarAtom,
  successMessageAtom,
} from '../../atoms/messageBarAtoms';
import { appRoles } from '../../authConfig';
import { useBoolean } from '@fluentui/react-hooks';
import ContractorSelector from '../../common/selectors/ContractorSelector';
import { FontIcon, Link, MessageBarType } from '@fluentui/react';
import { Briefcase24Regular, Tag24Regular } from '@fluentui/react-icons';
import AppContext from '../AppContext';
import WorkSelector from '../../common/selectors/WorkSelector';
import { useTranslation } from 'react-i18next';
import ConfirmDialog from '../../common/ConfirmDialog';
import { useFeatureFlag, Features } from '../../libs/featureAccess';
import WorkOfferPanel, { type IWorkOfferSubmission } from './WorkOfferPanel';

interface IWorkItem {
  id: number;
  workId?: number;
  code: string;
  description: string;
  status: string;
  statusId: number;
  createdAt: string;
  assetCategory?: string;
  zoneName?: string;
  orderId?: number;
  assigneeId?: number;
  assigneeName?: string;
  issueId: number;
  otherInfo?: string;
}

export interface WorksV2Params {
  TabCaption?: string;
  contractorId?: number;
  hideEditButtons?: boolean;
  hideExtraColumns?: boolean;
  showWorkAssignment?: boolean;
  /**
   * Draft assignment mode (used when creating a contractor that doesn't exist
   * yet). When provided, the grid renders these work ids as a local list -- no
   * refresh/filter/search/export, only an "Add" button and per-row remove --
   * and reports changes via {@link onDraftChange} instead of calling the
   * assignment API. Reuses the same grid/columns as the persisted edit view.
   */
  draftWorkIds?: number[];
  onDraftChange?: (ids: number[]) => void;
}

const WorksV2 = (params: WorksV2Params) => {
  const { t } = useTranslation();
  const { isTenantReady: assetsFeatureReady, isEnabled: assetsFeatureEnabled } =
    useFeatureFlag(Features.FEATURE_ASSETS);
  const { isTenantReady: zonesFeatureReady, isEnabled: zonesFeatureEnabled } =
    useFeatureFlag(Features.FEATURE_ZONES);
  const { isTenantReady: issuesFeatureReady, isEnabled: issuesFeatureEnabled } =
    useFeatureFlag(Features.FEATURE_ISSUES);
  const { isEnabled: showOffers } = useFeatureFlag(
    Features.FEATURE_WORK_OFFERS
  );

  const profileData = useAtomValue(profileDataAtom);
  const navigate = useNavigate();
  const setSuccessMessage = useSetAtom(successMessageAtom);
  const setMessageBar = useSetAtom(messageBarAtom);

  const [selectedWorkId, setSelectedWorkId] = useState<number>();
  const [
    isContractorSelectorOpen,
    { setTrue: showContractorSelector, setFalse: hideContractorSelector },
  ] = useBoolean(false);
  const [
    isWorkSelectorOpen,
    { setTrue: showWorkSelector, setFalse: hideWorkSelector },
  ] = useBoolean(false);

  const [workStatuses, setWorkStatuses] = useState<IWorkStatus[]>([]);
  const pagedDataGridRef = useRef<IPagedDataGridRefType>(null);

  const context = useContext(AppContext);
  const setErrorMessage = useSetAtom(errorMessageAtom);
  const setIsInProgress = useSetAtom(isInProgressAtom);
  const clearMessage = useSetAtom(clearMessageAtom);
  const [assigning, setAssigning] = useState(false);
  const [pendingAssignment, setPendingAssignment] =
    useState<IWorkAssignmentRequest | null>(null);
  const [pendingReassignment, setPendingReassignment] = useState<{
    contractorId: number;
    workIds: number[];
  } | null>(null);
  const [pendingContractorName, setPendingContractorName] = useState<
    string | null
  >(null);
  const [selectedWorkIds, setSelectedWorkIds] = useState<number[]>([]);
  const [
    isAssignConfirmOpen,
    { setTrue: showAssignConfirm, setFalse: hideAssignConfirm },
  ] = useBoolean(false);
  const [
    isDeleteConfirmOpen,
    { setTrue: showDeleteConfirm, setFalse: hideDeleteConfirm },
  ] = useBoolean(false);
  const [offerPanelOpen, setOfferPanelOpen] = useState(false);
  const [sendingOffers, setSendingOffers] = useState(false);
  const isDraftMode = params.draftWorkIds !== undefined;
  const draftWorkIds = useMemo(
    () => params.draftWorkIds ?? [],
    [params.draftWorkIds]
  );
  const draftIdsKey = draftWorkIds.join(',');
  // Cache of picked work rows, keyed by id. Rows come straight from the
  // WorkSelector on pick, so displaying the draft never needs an extra
  // works API call -- the only call is inside the selector when it's open.
  const [draftWorkCache, setDraftWorkCache] = useState<
    Record<number, IWorkItem>
  >({});
  // Displayed rows = the current draft ids resolved from the cache (removals
  // drop out automatically, order follows the id list).
  const draftWorks = useMemo(
    () =>
      draftWorkIds
        .map((id) => draftWorkCache[id])
        .filter((w): w is IWorkItem => w !== undefined),
    [draftWorkIds, draftWorkCache]
  );
  const [draftLoading, setDraftLoading] = useState(false);

  // Resolve only ids we don't already have a row for. This fires when the
  // component remounts (e.g. returning to the Assignments step) with draft ids
  // whose rows aren't cached yet -- NOT on every add/remove, since picked rows
  // are cached immediately from the selector.
  useEffect(() => {
    if (!isDraftMode) return;
    const missing = draftWorkIds.filter((id) => !draftWorkCache[id]);
    if (missing.length === 0) return;
    const abortController = new AbortController();
    setDraftLoading(true);
    const filter = JSON.stringify({
      filters: [
        {
          column: 'Id',
          operation: 'In',
          value: `(${missing.join(',')})`,
          type: 'Number',
        },
      ],
    });
    getWorks(abortController, missing.length, 1, undefined, undefined, filter)
      .then((data) => {
        if (!data?.items) return;
        setDraftWorkCache((prev) => {
          const next = { ...prev };
          data.items.forEach((wo: any) => {
            next[wo.id] = {
              ...wo,
              createdAt: formatDate(wo.createdAt, true),
            };
          });
          return next;
        });
      })
      .catch((error: any) => {
        if (error?.name === 'AbortError') return;
        setErrorMessage(error.message);
      })
      .finally(() => setDraftLoading(false));
    return () => abortController.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDraftMode, draftIdsKey]);

  const [metadataFields, setMetadataFields] = useState<IField[]>([]);
  const [metadataLookups, setMetadataLookups] = useState<ILookup[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      const abortController = new AbortController();
      setIsInProgress(true);

      try {
        const woStatuses: IWorkStatus[] = await getWorkStatuses(
          abortController
        );
        setWorkStatuses(woStatuses);
        const metadata = await getMetadata(abortController, 'Work');
        setMetadataFields(metadata?.fields ?? []);
        setMetadataLookups(metadata?.lookups ?? []);

        clearMessage();
        // Draft mode is embedded in the contractor create wizard, which owns
        // the page title -- don't fetch charts or override the tab here.
        if (!isDraftMode) {
          const chartData = await getChartsData(abortController, 'works', '');
          if (params.contractorId) {
            context.setSelectedTab(t('contractors'));
          } else {
            context.setSelectedTab(t('works'), chartData.values);
          }
        }
      } catch (error: any) {
        setErrorMessage(error.message);
      } finally {
        setIsInProgress(false);
      }
      return () => {
        abortController.abort();
      };
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const statusOptions = useMemo(
    () =>
      workStatuses.map((s) => ({
        value: String(s.id),
        label: s.name,
      })),
    [workStatuses]
  );

  const otherInfoColumns = useMemo<DataGridColumn<IWorkItem>[]>(
    () =>
      metadataFields
        .filter((f) => f.showInGrid)
        .map((f) => ({
          key: f.name,
          name: f.name,
          width: 150,
          minWidth: 120,
          type:
            f.fieldType === 'Date'
              ? ('date' as const)
              : f.fieldType === 'DateTime'
              ? ('datetime' as const)
              : undefined,
        })),
    [metadataFields]
  );

  const otherInfoFieldNames = useMemo(
    () => otherInfoColumns.map((c) => c.name ?? ''),
    [otherInfoColumns]
  );

  const customFilterColumns = useMemo(
    () => buildMetadataFilterColumns(metadataFields, metadataLookups),
    [metadataFields, metadataLookups]
  );

  const baseColumns: DataGridColumn<IWorkItem>[] = [
    {
      key: 'id',
      name: 'Id',
      minWidth: 50,
      isSortable: true,
      renderCell: (item: IWorkItem) => {
        const workId = params.showWorkAssignment ? item.workId : item.id;
        return (
          <Link onClick={() => navigate(`/editWork?id=${workId}`)}>
            {workId}
          </Link>
        );
      },
    },
    {
      key: 'code',
      name: 'Code',
      width: 80,
      isSortable: true,
    },
    {
      key: 'description',
      name: 'Description',
      minWidth: 250,
      isSortable: true,
      renderCell: (item) => (
        <span style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
          {item.description}
        </span>
      ),
    },
    {
      key: 'statusId',
      name: 'Status',
      width: 120,
      renderCell: (item) => (
        <StatusBadge
          showName
          mdl2Only
          statuses={workStatuses}
          statusId={item.statusId}
        />
      ),
    },
    {
      key: 'createdAt',
      name: 'Created at',
      width: 150,
    },
  ];

  const extraColumns: DataGridColumn<IWorkItem>[] = [
    {
      key: 'assigneeName',
      name: 'Assignee',
      width: 150,
      isSortable: true,
      renderCell: (item) =>
        item.assigneeId ? (
          <Link
            onClick={() => navigate(`/editContractor?id=${item.assigneeId}`)}
          >
            {item.assigneeName}
          </Link>
        ) : null,
    },
    {
      key: 'orderId',
      name: 'Order Id',
      width: 80,
      renderCell: (item) =>
        item.orderId ? (
          <Link onClick={() => navigate(`/editOrder?id=${item.orderId}`)}>
            {item.orderId}
          </Link>
        ) : null,
    },
    {
      key: 'assetCategory',
      name: 'Category',
      width: 100,
      ellipsis: true,
    },
    {
      key: 'zoneName',
      name: 'Zone',
      width: 100,
      ellipsis: true,
    },
    {
      key: 'issueId',
      name: 'Issue Id',
      width: 80,
      renderCell: (item) => (
        <Link onClick={() => navigate(`/editIssue?id=${item.issueId}`)}>
          {item.issueId}
        </Link>
      ),
    },
  ];

  const commandBarItems: ICommandBarItem[] = useMemo(
    () =>
      isDraftMode
        ? ([
            {
              key: 'assignWork',
              text: 'Add',
              icon: <FontIcon iconName="WorkItem" />,
              onClick: () => {
                showWorkSelector();
              },
            },
            {
              key: 'removeWork',
              text: 'Remove',
              icon: <FontIcon iconName="Delete" />,
              disabled: !selectedWorkId,
              onClick: () => {
                if (!selectedWorkId) return;
                params.onDraftChange?.(
                  draftWorkIds.filter((id) => id !== selectedWorkId)
                );
                setSelectedWorkId(undefined);
              },
            },
          ] as ICommandBarItem[])
        : params.showWorkAssignment
        ? [
            {
              key: 'refresh',
              text: 'Refresh',
              icon: <FontIcon iconName="Refresh" />,
              onClick: (ctx) => ctx.refresh(),
            },
            {
              key: 'assignWork',
              text: 'Add',
              icon: <FontIcon iconName="WorkItem" />,
              disabled:
                !profileData.roles.includes(appRoles.Admin) || assigning,
              onClick: () => {
                showWorkSelector();
              },
            },
            {
              key: 'reassignWork',
              text: 'Reassign',
              icon: <FontIcon iconName="PeopleAdd" />,
              disabled:
                !profileData.roles.includes(appRoles.Admin) ||
                selectedWorkIds.length === 0 ||
                assigning,
              onClick: () => {
                showContractorSelector();
              },
            },
            {
              key: 'export',
              text: 'Export',
              icon: <FontIcon iconName="Download" />,
              onClick: () => {},
            },
          ]
        : params.hideEditButtons
        ? [
            {
              key: 'refresh',
              text: 'Refresh',
              icon: <FontIcon iconName="Refresh" />,
              onClick: (ctx) => ctx.refresh(),
            },
            {
              key: 'export',
              text: 'Export',
              icon: <FontIcon iconName="Download" />,
              onClick: () => {},
            },
          ]
        : [
            {
              key: 'refresh',
              text: 'Refresh',
              icon: <FontIcon iconName="Refresh" />,
              onClick: (ctx) => ctx.refresh(),
            },
            {
              key: 'newWork',
              text: 'New',
              icon: <FontIcon iconName="Add" />,
              onClick: () => navigate('/newWork'),
              disabled: !profileData.roles.includes(appRoles.Admin),
            },
            {
              key: 'editWork',
              text: 'Edit',
              icon: <FontIcon iconName="Edit" />,
              onClick: () => navigate(`/editWork?id=${selectedWorkId}`),
              disabled:
                !profileData.roles.includes(appRoles.Admin) || !selectedWorkId,
            },
            {
              key: 'resendWork',
              text: 'Resend',
              icon: <FontIcon iconName="FabricSyncFolder" />,
              onClick: (ctx) => {
                if (selectedWorkId) {
                  const abortController = new AbortController();
                  deleteGroup(abortController, Number(selectedWorkId)).then(
                    () => {
                      ctx.refresh();
                      setSuccessMessage(
                        `Group with id: ${selectedWorkId} deleted.`
                      );
                    }
                  );
                }
              },
              disabled:
                !profileData.roles.includes(appRoles.Admin) || !selectedWorkId,
            },
            {
              key: 'assignWork',
              text: 'Assign',
              icon: <FontIcon iconName="Assign" />,
              disabled:
                !profileData.roles.includes(appRoles.Admin) ||
                !selectedWorkId ||
                assigning,
              onClick: () => {
                showContractorSelector();
              },
            },
            ...(showOffers
              ? [
                  {
                    key: 'offerWork',
                    text: t('offer', 'Offer'),
                    icon: <Tag24Regular />,
                    disabled:
                      !profileData.roles.includes(appRoles.Admin) ||
                      !selectedWorkId ||
                      sendingOffers,
                    onClick: () => setOfferPanelOpen(true),
                  } as ICommandBarItem,
                ]
              : []),
            {
              key: 'deleteWork',
              text: 'Delete',
              icon: <FontIcon iconName="Delete" />,
              onClick: () => showDeleteConfirm(),
              disabled:
                !profileData.roles.includes(appRoles.Admin) || !selectedWorkId,
            },
            {
              key: 'export',
              text: 'Export',
              icon: <FontIcon iconName="Download" />,
              onClick: () => {},
            },
          ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      isDraftMode,
      draftIdsKey,
      params.showWorkAssignment,
      params.hideEditButtons,
      params.onDraftChange,
      profileData.roles,
      assigning,
      selectedWorkId,
      selectedWorkIds,
      navigate,
      showWorkSelector,
      setSuccessMessage,
      showContractorSelector,
      showDeleteConfirm,
      showOffers,
      sendingOffers,
      t,
    ]
  );

  const handleSendOffers = useCallback(
    async (submission: IWorkOfferSubmission) => {
      if (!selectedWorkId) return;
      setSendingOffers(true);
      try {
        const abortController = new AbortController();
        const response = await createWorkOfferBatch(abortController, {
          workId: selectedWorkId,
          contractorIds: submission.contractorIds,
          message: submission.message,
          offeredPrice: submission.offeredPrice,
        });
        pagedDataGridRef.current?.refresh();
        setOfferPanelOpen(false);
        setSuccessMessage(
          t('offersSent', '{{count}} offer(s) sent.', {
            count: response.workOfferIds.length,
          })
        );
      } catch (err) {
        setErrorMessage((err as Error).message);
      } finally {
        setSendingOffers(false);
      }
    },
    [selectedWorkId, setSuccessMessage, setErrorMessage, t]
  );

  const columns = useMemo(
    () => {
      const visibleExtraColumns = extraColumns.filter((column) => {
        if (column.key === 'assetCategory') {
          return assetsFeatureReady && assetsFeatureEnabled;
        }

        if (column.key === 'zoneName') {
          return zonesFeatureReady && zonesFeatureEnabled;
        }

        if (column.key === 'issueId') {
          return issuesFeatureReady && issuesFeatureEnabled;
        }

        return true;
      });

      return [
        ...baseColumns,
        ...(!params.hideExtraColumns ? visibleExtraColumns : []),
        ...(!params.hideExtraColumns ? otherInfoColumns : []),
      ];
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      workStatuses,
      statusOptions,
      otherInfoColumns,
      params.hideExtraColumns,
      assetsFeatureReady,
      assetsFeatureEnabled,
      zonesFeatureReady,
      zonesFeatureEnabled,
      issuesFeatureReady,
      issuesFeatureEnabled,
    ]
  );

  const filterColumns: IFilterableColumn[] = useMemo(
    () => [
      { key: 'id', label: 'Id', type: FilterType.String },
      { key: 'code', label: 'Code', type: FilterType.String },
      { key: 'description', label: 'Description', type: FilterType.String },
      {
        key: 'statusId',
        label: 'Status',
        type: FilterType.Number,
        operators: [
          FilterOperator.Eq,
          FilterOperator.NEq,
          FilterOperator.NNul,
          FilterOperator.In,
        ],
        options: statusOptions,
      },
      { key: 'createdAt', label: 'Created at', type: FilterType.Date },
      {
        key: 'isCompleted',
        label: 'Is Completed',
        type: FilterType.Number,
        toggleOptions: [{ value: '1' }, { value: '0' }],
      },
      { key: 'assigneeName', label: 'Assignee', type: FilterType.String },
      ...customFilterColumns,
    ],
    [statusOptions, customFilterColumns]
  );

  return (
    <div style={{ maxWidth: 1200 }}>
      <PagedDataGrid<IWorkItem>
        ref={pagedDataGridRef}
        columns={columns}
        {...(isDraftMode
          ? { items: draftWorks, loading: draftLoading, searchable: false }
          : { filterEnabled: true, filterColumns })}
        getAction={async (
          abortController,
          pageSize,
          pageNo,
          orderBy,
          search,
          filters
        ) => {
          const data = !params.contractorId
            ? await getWorks(
                abortController,
                pageSize,
                pageNo,
                orderBy,
                search,
                filters
              )
            : await getWorkAssignment(
                abortController,
                params.contractorId,
                pageSize,
                pageNo,
                search
              );
          data.items = data.items.map((wo: any) => ({
            ...wo,
            createdAt: formatDate(wo.createdAt, true),
          }));
          return data;
        }}
        commandBarItems={commandBarItems}
        selectionMode={params.showWorkAssignment ? 'multiselect' : 'single'}
        onSelectionChange={(selection) => {
          setSelectedWorkId(Number(selection.id));
        }}
        onMultiSelectionChange={(selection) => {
          setSelectedWorkIds(
            selection.map((item) => Number(item.workId ?? item.id))
          );
        }}
        onRowDoubleClicked={(selection) => {
          const workId = params.showWorkAssignment
            ? selection.workId
            : selection.id;
          navigate(`/editWork?id=${workId}`);
        }}
        listHeigh={params.contractorId ? 425 : 525}
        loadingConfig={{
          message: t('loadingWorks') || 'Loading works...',
          icon: (
            <Briefcase24Regular
              style={{ width: 125, height: 125, color: 'lightgray' }}
            />
          ),
        }}
        otherInfoFieldNames={otherInfoFieldNames}
      />
      <ContractorSelector
        isOpen={isContractorSelectorOpen}
        hideContractorSelector={hideContractorSelector}
        onContractorSelected={(id: number, name: string) => {
          if (params.showWorkAssignment) {
            if (selectedWorkIds.length === 0) return;
            hideContractorSelector();
            setPendingReassignment({
              contractorId: id,
              workIds: selectedWorkIds,
            });
            setPendingContractorName(name);
            showAssignConfirm();
            return;
          }
          if (!selectedWorkId) return;
          hideContractorSelector();
          setPendingAssignment({
            contractorId: id,
            workId: selectedWorkId,
            description: '',
          });
          setPendingContractorName(name);
          showAssignConfirm();
        }}
      />
      <WorkSelector
        isOpen={isWorkSelectorOpen}
        hideWorkSelector={hideWorkSelector}
        onWorkSelected={(id: number, work?: IWork) => {
          hideWorkSelector();
          // Draft mode: append to the local list, no assignment API call. Cache
          // the picked row so rendering never needs to re-fetch it.
          if (isDraftMode) {
            if (work) {
              setDraftWorkCache((prev) => ({
                ...prev,
                [id]: {
                  ...(work as unknown as IWorkItem),
                  createdAt: work.createdAt
                    ? formatDate(work.createdAt, true)
                    : '',
                },
              }));
            }
            if (!draftWorkIds.includes(id)) {
              params.onDraftChange?.([...draftWorkIds, id]);
            }
            return;
          }
          setPendingAssignment({
            contractorId: params.contractorId ?? 0,
            workId: id,
            description: '',
          });
          showAssignConfirm();
        }}
      />
      <ConfirmDialog
        isModalOpen={isAssignConfirmOpen}
        hideModal={() => {
          hideAssignConfirm();
          setPendingAssignment(null);
          setPendingReassignment(null);
          setPendingContractorName(null);
        }}
        message={
          pendingReassignment
            ? `${
                pendingReassignment.workIds.length
              } work(s) will be reassigned${
                pendingContractorName ? ` to "${pendingContractorName}"` : ''
              }.`
            : pendingAssignment
            ? `Work ${pendingAssignment.workId} will be assigned${
                pendingContractorName ? ` to "${pendingContractorName}"` : ''
              }.`
            : 'Work will be assigned.'
        }
        onYesClick={async () => {
          const assignment = pendingAssignment;
          const reassignment = pendingReassignment;
          hideAssignConfirm();
          setPendingAssignment(null);
          setPendingReassignment(null);
          setPendingContractorName(null);
          if (!assignment && !reassignment) return;

          try {
            setMessageBar(
              reassignment ? 'Reassigning' : 'Assigning',
              MessageBarType.info
            );
            setAssigning(true);
            const abortController = new AbortController();
            if (reassignment) {
              await updateWorkAssignments(
                abortController,
                reassignment.contractorId,
                { workIds: reassignment.workIds, description: '' }
              );
              setSelectedWorkIds([]);
              setSuccessMessage('Work(s) reassigned.');
            } else if (assignment) {
              await createWorkAssignment(abortController, assignment);
              setSuccessMessage('Work assigned.');
            }
            pagedDataGridRef.current?.refresh();
          } catch (error: any) {
            setErrorMessage(error.message);
          } finally {
            setAssigning(false);
          }
        }}
      />
      <ConfirmDialog
        isModalOpen={isDeleteConfirmOpen}
        hideModal={hideDeleteConfirm}
        message={`The Work ${selectedWorkId} would be deleted, are you sure?`}
        onYesClick={() => {
          hideDeleteConfirm();
          if (!selectedWorkId) return;
          const abortController = new AbortController();
          deleteWork(abortController, Number(selectedWorkId))
            .then(() => {
              pagedDataGridRef.current?.refresh();
              setSuccessMessage(`Work ${selectedWorkId} successfully deleted`);
            })
            .catch((error: any) => {
              setErrorMessage(error.message);
            });
        }}
      />
      <WorkOfferPanel
        isOpen={offerPanelOpen}
        sending={sendingOffers}
        onDismiss={() => setOfferPanelOpen(false)}
        onSendOffers={handleSendOffers}
      />
    </div>
  );
};

export default WorksV2;
